import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';

const POLICIES = {
  'terms-and-conditions': {
    title: 'Terms and Conditions',
    lastUpdated: 'April 01, 2026',
    intro: 'These Terms and Conditions ("Terms") govern your use of the Marema Consulting website and services. By accessing or using this website, you agree to be bound by these Terms.',
    sections: [
      {
        heading: '1. Company Information',
        content: `Marema Consulting (Pty) Ltd\nTrading as: Marema Consulting\nOffice no. 555, 5th Floor, 288 on Kent, 288 Kent Avenue, Ferndale, Randburg, Gauteng, 2194\nPhone: +27 10 500 9477\nEmail: info@maremaconsulting.co.za`
      },
      {
        heading: '2. Nature of Services',
        content: 'Marema Consulting provides professional consulting services, which may include:',
        list: ['Business consulting', 'Compliance advisory', 'Enterprise and supplier development', 'Strategic and operational advisory services'],
        after: 'All services are subject to separate written agreements, proposals, or service-level agreements (SLAs).'
      },
      {
        heading: '3. Use of the Website',
        content: 'By using this website, you agree:',
        list: ['Not to use the website for unlawful purposes', 'Not to attempt to gain unauthorised access to systems or data', 'Not to copy, reproduce, or distribute content without permission', 'To provide accurate information when submitting forms'],
        after: 'We reserve the right to restrict or terminate access for misuse.'
      },
      {
        heading: '4. No Professional Advice',
        content: 'The content on this website is provided for general informational purposes only and does not constitute:',
        list: ['Legal advice', 'Financial advice', 'Compliance certification'],
        after: 'You should engage Marema Consulting formally or consult a qualified professional before making decisions based on website content.'
      },
      {
        heading: '5. Service Engagement',
        list: ['All services require formal acceptance of a quotation, proposal, or agreement', 'Scope, timelines, deliverables, and fees will be defined in writing', 'Service turnaround time is 30-90 days depending on service type and municipality', 'No work will commence without mutual agreement']
      },
      {
        heading: '6. Fees and Payment',
        list: ['Fees are agreed upon in writing prior to service delivery', 'Payment terms will be specified in the agreement or invoice', 'Late payments may result in suspension of services']
      },
      {
        heading: '7. Intellectual Property',
        content: 'All website content, including text, graphics, logos, and documents, is the property of Marema Consulting unless otherwise stated. You may not reproduce, distribute, or use any content without prior written consent.'
      },
      {
        heading: '8. Confidentiality',
        content: 'We respect client confidentiality and will not disclose sensitive information unless:',
        list: ['Required by law', 'Authorised by the client', 'Necessary to deliver services (with appropriate safeguards)'],
        after: 'Clients are also expected to maintain confidentiality regarding proprietary methodologies and materials provided.'
      },
      {
        heading: '9. Limitation of Liability',
        content: 'To the fullest extent permitted by law, Marema Consulting shall not be liable for:',
        list: ['Indirect or consequential losses', 'Loss of profits or business opportunities', 'Decisions made based on website information'],
        after: 'All services are provided in good faith, but outcomes depend on various external factors beyond our control.'
      },
      {
        heading: '10. Indemnity',
        content: 'You agree to indemnify and hold harmless Marema Consulting from any claims, damages, or losses arising from:',
        list: ['Your misuse of the website', 'Your breach of these Terms', 'Your reliance on information without professional engagement']
      },
      {
        heading: '11. Third-Party Links',
        content: 'This website may include links to third-party websites. We are not responsible for their content, accuracy, or practices.'
      },
      {
        heading: '12. Data Protection',
        content: 'Your personal information is handled in accordance with our Privacy Policy and the Protection of Personal Information Act.'
      },
      {
        heading: '13. Changes to Terms',
        content: 'We reserve the right to update or modify these Terms at any time. Changes will be effective upon publication on this website.'
      },
      {
        heading: '14. Governing Law',
        content: 'These Terms are governed by the laws of South Africa. Any disputes arising from these Terms shall be subject to the jurisdiction of South African courts.'
      }
    ]
  },
  'privacy-policy': {
    title: 'Privacy Policy',
    lastUpdated: 'April 01, 2026',
    intro: 'Marema Consulting (Pty) Ltd ("we", "us", or "our") respects your privacy and is committed to protecting your personal information in accordance with the Protection of Personal Information Act (POPIA). This Privacy Policy explains how we collect, use, and protect your information when you visit maremaconsulting.co.za.',
    sections: [
      {
        heading: '1. Information We Collect',
        content: 'We may collect and process the following personal information:',
        list: ['Full name', 'Email address', 'Contact number', 'Company or organisation details', 'Any information you provide through forms, email, or direct communication'],
        after: 'We collect this information when you submit a contact or enquiry form, request a consultation or quote, or communicate with us via email or phone.'
      },
      {
        heading: '2. How We Use Your Information',
        content: 'Your personal information is used to:',
        list: ['Respond to enquiries and provide requested services', 'Communicate with you regarding your request', 'Deliver consulting and advisory services', 'Improve our website and service offering', 'Maintain internal records']
      },
      {
        heading: '3. Legal Basis for Processing',
        content: 'We process your information in accordance with POPIA based on:',
        list: ['Your consent', 'The performance of a service or contract', 'Compliance with legal obligations', 'Legitimate business interests']
      },
      {
        heading: '4. Sharing of Information',
        content: 'We do not sell, rent, or trade your personal information. We may share your information with:',
        list: ['Employees or authorised representatives of Marema Consulting', 'Third-party service providers who assist in business operations', 'Regulatory or legal authorities, where required by law'],
        after: 'All third parties are required to respect the confidentiality of your information.'
      },
      {
        heading: '5. Data Security',
        content: 'We take reasonable technical and organisational measures to protect your personal information against loss, unauthorised access, misuse or disclosure. However, no method of transmission over the internet is completely secure.'
      },
      {
        heading: '6. Data Retention',
        content: 'We retain personal information only for as long as necessary to fulfil the purpose for which it was collected or as required by law or regulatory obligations.'
      },
      {
        heading: '7. Your Rights (Under POPIA)',
        content: 'You have the right to:',
        list: ['Access your personal information', 'Request correction or deletion', 'Object to processing of your data', 'Withdraw consent at any time', 'Lodge a complaint with the Information Regulator']
      },
      {
        heading: '8. Cookies and Website Usage',
        content: 'Our website may use cookies to enhance user experience, analyse website traffic, and improve functionality. You can disable cookies through your browser settings.'
      },
      {
        heading: '9. Third-Party Links',
        content: 'Our website may contain links to external websites. We are not responsible for the privacy practices of those websites.'
      },
      {
        heading: '10. Changes to This Policy',
        content: 'We may update this Privacy Policy from time to time. Updates will be published on this page with a revised Last Updated date.'
      },
      {
        heading: '11. Contact Details',
        content: 'If you have any questions or requests regarding your personal information, please contact us:\n\nMarema Consulting (Pty) Ltd\nOffice no. 555, 5th Floor, 288 on Kent, 288 Kent Avenue, Ferndale, Randburg, Gauteng, 2194\nPhone: +27 10 500 9477\nEmail: info@maremaconsulting.co.za'
      }
    ]
  },
  'paia-manual': {
    title: 'PAIA Manual',
    lastUpdated: 'April 01, 2026',
    intro: 'Promotion of Access to Information Act (PAIA) Manual — Marema Consulting (Pty) Ltd t/a Marema Consulting. Prepared in terms of Section 51 of the Promotion of Access to Information Act.',
    sections: [
      {
        heading: '1. Introduction',
        content: 'The Promotion of Access to Information Act gives effect to the constitutional right of access to information held by private bodies. This manual outlines how to request access to information from Marema Consulting.'
      },
      {
        heading: '2. Company Details',
        content: 'Marema Consulting (Pty) Ltd\nOffice no. 555, 5th Floor, 288 on Kent, 288 Kent Avenue, Ferndale, Randburg, Gauteng, 2194\nPhone: +27 10 500 9477\nEmail: info@maremaconsulting.co.za'
      },
      {
        heading: '3. Information Officer',
        content: 'The Information Officer is responsible for ensuring compliance with PAIA and handling information requests.\n\nInformation Officer: Director (Marema Consulting)\nEmail: info@maremaconsulting.co.za'
      },
      {
        heading: '4. Guide on How to Use PAIA',
        content: 'A guide on how to exercise rights under PAIA is available from the Information Regulator at https://www.justice.gov.za/inforeg/'
      },
      {
        heading: '5. Records Available Without Request',
        content: 'The following records may be available without a formal request:',
        list: ['Website content', 'Marketing materials', 'Service information']
      },
      {
        heading: '6. Records Held by the Company',
        content: 'Company Records: Registration documents, financial records, tax records.\n\nOperational Records: Client agreements, proposals and reports, internal policies and procedures.\n\nEmployee Records (if applicable): Employment contracts, HR policies.'
      },
      {
        heading: '7. Request Procedure',
        content: 'To request access to records:',
        list: ['Submit a written request to the Information Officer', 'Clearly describe the record you are requesting', 'Provide proof of identity', 'Pay any applicable request fees (if required)']
      },
      {
        heading: '8. Grounds for Refusal',
        content: 'Access to information may be refused where:',
        list: ['It involves personal information of third parties', 'It is confidential or commercially sensitive', 'It is legally privileged']
      },
      {
        heading: '9. Remedies Available',
        content: 'If your request is denied, you may:',
        list: ['Lodge a complaint with the Information Regulator', 'Approach a court of law']
      },
      {
        heading: '10. Availability of the Manual',
        content: 'This PAIA Manual is available on the Marema Consulting website and upon request from the company.'
      }
    ]
  },
  'disclaimer': {
    title: 'Disclaimer',
    lastUpdated: 'April 01, 2026',
    intro: 'The information provided on this website (maremaconsulting.co.za) is for general informational purposes only. By accessing and using this website, you accept and agree to the terms of this Disclaimer.',
    sections: [
      {
        heading: '1. No Professional Advice',
        content: 'The content on this website does not constitute:',
        list: ['Legal advice', 'Financial advice', 'Compliance certification', 'Professional consulting advice'],
        after: 'While Marema Consulting provides professional services, any information on this website should not be relied upon as a substitute for formal engagement. Users are encouraged to obtain professional advice tailored to their specific business needs.'
      },
      {
        heading: '2. Accuracy of Information',
        content: 'We strive to ensure that all information on this website is accurate and up to date. However:',
        list: ['We make no warranties or representations regarding completeness, accuracy, or reliability', 'Information may change without notice', 'Errors or omissions may occur']
      },
      {
        heading: '3. No Guarantees of Results',
        content: 'Marema Consulting aims to deliver measurable value through its services. However:',
        list: ['Outcomes depend on client implementation', 'External factors such as market conditions and regulatory changes may affect results', 'We do not guarantee specific business outcomes or results']
      },
      {
        heading: '4. Limitation of Liability',
        content: 'To the fullest extent permitted by law, Marema Consulting shall not be liable for:',
        list: ['Any direct, indirect, or consequential loss', 'Loss of profits, revenue, or business opportunities', 'Any damages arising from reliance on website content'],
        after: 'Your use of the website and reliance on any information is entirely at your own risk.'
      },
      {
        heading: '5. External Links Disclaimer',
        content: 'This website may contain links to third-party websites.',
        list: ['These links are provided for convenience only', 'We do not control or endorse the content of third-party sites', 'We are not responsible for their accuracy, security, or privacy practices']
      },
      {
        heading: '6. Availability of Website',
        content: 'We do not guarantee that the website will always be available or free from errors, interruptions, or viruses. We reserve the right to modify or discontinue the website at any time without notice.'
      },
      {
        heading: '7. Use at Your Own Risk',
        content: 'All use of this website is at your own risk. You are responsible for ensuring that any information, services, or content available meets your specific requirements.'
      },
      {
        heading: '8. Intellectual Property Notice',
        content: 'All content on this website remains the property of Marema Consulting unless otherwise stated. Unauthorised use, reproduction, or distribution is prohibited.'
      },
      {
        heading: '9. Governing Law',
        content: 'This Disclaimer is governed by the laws of South Africa.'
      },
      {
        heading: '10. Contact Details',
        content: 'If you have any questions regarding this Disclaimer, please contact:\n\nMarema Consulting (Pty) Ltd\nOffice no. 555, 5th Floor, 288 on Kent, 288 Kent Avenue, Ferndale, Randburg, Gauteng, 2194\nPhone: +27 10 500 9477\nEmail: info@maremaconsulting.co.za'
      }
    ]
  },
  'cookies-policy': {
    title: 'Cookies Policy',
    lastUpdated: 'April 01, 2026',
    intro: 'This Cookie Policy explains how Marema Consulting uses cookies and similar technologies on its website (maremaconsulting.co.za).',
    sections: [
      {
        heading: '1. What Are Cookies?',
        content: 'Cookies are small text files stored on your device when you visit a website. They help improve your browsing experience and allow websites to function efficiently.'
      },
      {
        heading: '2. How We Use Cookies',
        content: 'We use cookies to:',
        list: ['Ensure the website functions properly', 'Improve user experience', 'Analyse website traffic and performance', 'Remember user preferences (where applicable)']
      },
      {
        heading: '3. Types of Cookies We Use',
        content: 'Essential Cookies: Required for the website to function properly.\n\nAnalytics Cookies: Help us understand how visitors interact with the website (e.g., pages visited, time spent).\n\nFunctional Cookies: Allow the website to remember your preferences.'
      },
      {
        heading: '4. Third-Party Cookies',
        content: 'We may use trusted third-party services (such as analytics tools) that place cookies on your device. These third parties have their own privacy policies governing their use of cookies.'
      },
      {
        heading: '5. Managing Cookies',
        content: 'You can manage or disable cookies through your browser settings. Please note that disabling cookies may affect website functionality.'
      },
      {
        heading: '6. Consent',
        content: 'By continuing to use this website, you consent to the use of cookies in accordance with this policy.'
      },
      {
        heading: '7. Updates to This Policy',
        content: 'We may update this Cookie Policy from time to time. Changes will be posted on this page.'
      },
      {
        heading: '8. Contact Details',
        content: 'Marema Consulting (Pty) Ltd\nPhone: +27 10 500 9477\nEmail: info@maremaconsulting.co.za'
      }
    ]
  }
};

const PolicyPage = () => {
  const location = useLocation();
  const policySlug = location.pathname.replace(/^\//, '');
  const policy = POLICIES[policySlug];

  if (!policy) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="pt-20 pb-20 text-center">
          <h1 className="text-4xl font-light text-gray-800 mb-4">Page Not Found</h1>
          <Link to="/" className="text-[#4CAF50] hover:underline">Return to Home</Link>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Banner */}
      <section className="bg-[#1a2332] pt-20">
        <div className="py-16">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <div className="w-12 h-1 bg-[#4CAF50] mx-auto mb-6"></div>
            <h1 className="text-4xl sm:text-5xl font-light text-white" data-testid="policy-title">{policy.title}</h1>
          </div>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-16 bg-white" data-testid="policy-content">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="text-sm text-gray-500 mb-2">Marema Consulting (Pty) Ltd t/a Marema Consulting</p>
          <p className="text-sm text-gray-500 mb-8">Last Updated: {policy.lastUpdated}</p>

          <p className="text-gray-700 leading-relaxed mb-10">{policy.intro}</p>

          {policy.sections.map((section, index) => (
            <div key={index} className="mb-10">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">{section.heading}</h2>
              {section.content && (
                <p className="text-gray-600 leading-relaxed whitespace-pre-line mb-3">{section.content}</p>
              )}
              {section.list && (
                <ul className="list-disc list-inside text-gray-600 space-y-1 mb-3 ml-4">
                  {section.list.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              )}
              {section.after && (
                <p className="text-gray-600 leading-relaxed mt-3">{section.after}</p>
              )}
              {index < policy.sections.length - 1 && (
                <hr className="mt-8 border-gray-200" />
              )}
            </div>
          ))}

          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <Link to="/contact" className="inline-block bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none px-8 py-3 font-medium transition-colors" data-testid="policy-get-quote-link">
              Get A Quote
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default PolicyPage;
